'use client';

import React from 'react';

/**
 * Hook that alerts whether scroll bar is visible or not
 */
interface ScrollbarVisible {
  horizontal: boolean;
  vertical: boolean;
}
export const useScrollBarVisible = (element: HTMLElement | null): ScrollbarVisible => {
  const getScrollValues = React.useCallback((): ScrollbarVisible => {
    if (element === null) {
      return { horizontal: false, vertical: false };
    } else {
      const vertical = element.scrollHeight > element.clientHeight;
      const horizontal = element.scrollWidth > element.clientWidth;
      return { horizontal, vertical };
    }
  }, [element]);

  const [scrollbarVisible, setScrollbar] = React.useState(() => getScrollValues());

  const scrollValue = React.useCallback(() => {
    setScrollbar(old => {
      const newScrollbarVisible = getScrollValues();
      if (newScrollbarVisible.horizontal !== old.horizontal || newScrollbarVisible.vertical !== old.vertical) {
        return newScrollbarVisible;
      } else {
        return old;
      }
    });
  }, [getScrollValues]);

  React.useEffect(() => {
    const resizeObserver = new ResizeObserver(entries => {
      if (!Array.isArray(entries) || !entries.length) {
        return;
      }
      scrollValue();
    });

    if (element) {
      resizeObserver.observe(element);
    }
    return () => resizeObserver.disconnect();
  }, [element, scrollValue]);

  return scrollbarVisible;
};
